/**
 * AdminEwkKategorien — Stammdaten: EWK-Kategorien (Einwegkunststofffonds).
 * Abgabesätze nach EWKFondsV in €/kg. Kein Löschen (keine Policy) —
 * Kategorien werden per aktiv=false deaktiviert.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Recycle, Plus, Search, Pencil, Loader2, AlertTriangle } from 'lucide-react';

const QUERY_KEY = ['ewk-kategorien'];
const MIGRATION_HINT = 'Migration 20260907_03_ewk_gewicht_grundpreis.sql ausführen oder Kategorie anlegen.';
const SCHLUESSEL_RE = /^[a-z0-9_]+$/;
const EMPTY_FORM = { schluessel: '', name: '', abgabesatz_eur_kg: '', gueltig_ab: '', position: '', notes: '', aktiv: true };

const satzFmt = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const formatSatz = (v) => (v === null || v === undefined || v === '' ? null : satzFmt.format(Number(v)));
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : '–');
const parseSatz = (v) => {
  const s = String(v ?? '').trim();
  return s === '' ? null : parseFloat(s.replace(',', '.'));
};

const sortKategorien = (rows) => [...rows].sort((a, b) =>
  (a.position ?? 0) - (b.position ?? 0) || (a.name || '').localeCompare(b.name || '', 'de'));

/** Liefert Fehlertext oder null. Eindeutigkeit wird gegen die geladenen Zeilen geprüft. */
function validateForm(form, rows, editing) {
  const key = form.schluessel.trim();
  if (!editing) {
    if (!SCHLUESSEL_RE.test(key)) return 'Schlüssel: nur a-z, 0-9 und _';
    if (rows.some((r) => r.schluessel === key)) return 'Schlüssel bereits vergeben';
  }
  if (!form.name.trim()) return 'Bitte einen Namen eingeben.';
  const satz = parseSatz(form.abgabesatz_eur_kg);
  if (satz !== null && Number.isNaN(satz)) return 'Satz ist keine gültige Zahl.';
  if (satz !== null && satz < 0) return 'Satz darf nicht negativ sein';
  return null;
}

function toPayload(form) {
  return {
    schluessel: form.schluessel.trim(),
    name: form.name.trim(),
    abgabesatz_eur_kg: parseSatz(form.abgabesatz_eur_kg),
    gueltig_ab: form.gueltig_ab || null,
    position: form.position === '' ? 0 : parseInt(form.position, 10),
    notes: form.notes.trim() || null,
    aktiv: form.aktiv,
  };
}

function SatzCell({ value }) {
  const text = formatSatz(value);
  if (text === null) {
    return <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-[10px]">Satz fehlt</Badge>;
  }
  return <span className="tabular-nums font-medium">{text}</span>;
}

function InfoCard() {
  return (
    <Card className="border-slate-200 bg-white">
      <CardContent className="p-4 text-sm text-slate-600 space-y-1">
        <p>Hersteller melden jährlich bis 15. Mai die in Verkehr gebrachte Masse je Produktart an DIVID (Umweltbundesamt).</p>
        <p>Abgabe = Masse (kg) × Satz. Die Sätze werden im Produkt-Editor für die Abgabe-Vorschau verwendet.</p>
        <a href="https://www.einwegkunststofffonds.de" target="_blank" rel="noreferrer" className="text-brand-900 underline underline-offset-2 font-medium">
          einwegkunststofffonds.de
        </a>
      </CardContent>
    </Card>
  );
}

function KategorieTable({ rows, onToggle, onEdit }) {
  return (
    <Card className="hidden md:block overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-right w-16">Pos.</TableHead>
            <TableHead>Schlüssel</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="text-right">Satz €/kg</TableHead>
            <TableHead>gültig ab</TableHead>
            <TableHead className="w-20">Aktiv</TableHead>
            <TableHead className="text-right w-20">Aktionen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((k) => (
            <TableRow key={k.id} className={cn('h-12', !k.aktiv && 'opacity-60')}>
              <TableCell className="text-right tabular-nums text-slate-500">{k.position ?? 0}</TableCell>
              <TableCell className="font-mono text-xs">{k.schluessel}</TableCell>
              <TableCell className="font-medium text-slate-900">{k.name}</TableCell>
              <TableCell className="text-right"><SatzCell value={k.abgabesatz_eur_kg} /></TableCell>
              <TableCell className="tabular-nums text-slate-600">{formatDate(k.gueltig_ab)}</TableCell>
              <TableCell>
                <Switch checked={!!k.aktiv} onCheckedChange={(v) => onToggle(k, v)} aria-label={`${k.name} aktiv`} />
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => onEdit(k)} aria-label="Bearbeiten">
                  <Pencil className="w-4 h-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function KategorieCards({ rows, onToggle, onEdit }) {
  return (
    <div className="md:hidden space-y-3">
      {rows.map((k) => (
        <Card key={k.id} className={cn(!k.aktiv && 'opacity-60')}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-slate-900 truncate">{k.name}</p>
                <p className="font-mono text-xs text-slate-500 truncate">{k.schluessel}</p>
              </div>
              <div className="text-right shrink-0">
                <SatzCell value={k.abgabesatz_eur_kg} />
                <p className="text-[10px] text-slate-400 mt-0.5">€/kg</p>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="tabular-nums">Pos. {k.position ?? 0} · gültig ab {formatDate(k.gueltig_ab)}</span>
              <div className="flex items-center gap-1">
                <Switch checked={!!k.aktiv} onCheckedChange={(v) => onToggle(k, v)} aria-label={`${k.name} aktiv`} />
                <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => onEdit(k)} aria-label="Bearbeiten">
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function KategorieDialog({ open, editing, form, setForm, onClose, onSave, isPending }) {
  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Recycle className="w-5 h-5 text-brand-900" />
            {editing ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold">Schlüssel *</Label>
              <Input value={form.schluessel} onChange={f('schluessel')} disabled={!!editing} placeholder="z.B. getraenkebecher"
                className="h-11 font-mono" autoFocus={!editing} />
              {!editing && <p className="text-[10px] text-slate-400">Nur a-z, 0-9 und _ · später nicht änderbar</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold">Position</Label>
              <Input type="number" value={form.position} onChange={f('position')} className="h-11 tabular-nums" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold">Name *</Label>
            <Input value={form.name} onChange={f('name')} placeholder="z.B. Getränkebecher" className="h-11 font-medium" autoFocus={!!editing} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold">Abgabesatz €/kg</Label>
              <Input inputMode="decimal" value={form.abgabesatz_eur_kg} onChange={f('abgabesatz_eur_kg')} placeholder="z.B. 1,236"
                className="h-11 tabular-nums text-right" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold">Gültig ab</Label>
              <Input type="date" value={form.gueltig_ab} onChange={f('gueltig_ab')} className="h-11" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold">Notizen</Label>
            <Textarea value={form.notes} onChange={f('notes')} rows={3} placeholder="Interne Hinweise, Rechtsgrundlage, …" />
          </div>

          {editing && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/50 px-3 h-11">
              <Label htmlFor="ewk-aktiv" className="text-xs font-bold">Aktiv</Label>
              <Switch id="ewk-aktiv" checked={form.aktiv} onCheckedChange={(v) => setForm((p) => ({ ...p, aktiv: v }))} />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="h-11">Abbrechen</Button>
          <Button onClick={onSave} disabled={isPending} className="bg-brand-950 hover:bg-brand-900 text-white font-bold h-11 px-6 gap-2">
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminEwkKategorien() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => base44.entities.EwkKategorie.list('position'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const toggleMut = useMutation({
    mutationFn: ({ id, aktiv }) => base44.entities.EwkKategorie.update(id, { aktiv, updated_date: new Date().toISOString() }),
    onSuccess: (_, { aktiv }) => { invalidate(); toast.success(aktiv ? 'Kategorie aktiviert' : 'Kategorie deaktiviert'); },
    onError: (e) => toast.error('Fehler: ' + e.message),
  });

  const saveMut = useMutation({
    mutationFn: ({ id, values }) => (id
      ? base44.entities.EwkKategorie.update(id, { ...values, updated_date: new Date().toISOString() })
      : base44.entities.EwkKategorie.create({ ...values, aktiv: true })),
    onSuccess: () => { invalidate(); toast.success('Kategorie gespeichert'); closeDialog(); },
    onError: (e) => toast.error('Fehler: ' + e.message),
  });

  const closeDialog = () => { setDialogOpen(false); setEditing(null); setForm({ ...EMPTY_FORM }); };

  const openDialog = (k = null) => {
    if (k) {
      setEditing(k);
      setForm({
        schluessel: k.schluessel || '',
        name: k.name || '',
        abgabesatz_eur_kg: k.abgabesatz_eur_kg == null ? '' : String(k.abgabesatz_eur_kg).replace('.', ','),
        gueltig_ab: k.gueltig_ab ? String(k.gueltig_ab).slice(0, 10) : '',
        position: k.position ?? '',
        notes: k.notes || '',
        aktiv: !!k.aktiv,
      });
    } else {
      const nextPos = rows.reduce((m, r) => Math.max(m, r.position ?? 0), 0) + 1;
      setEditing(null);
      setForm({ ...EMPTY_FORM, position: String(nextPos) });
    }
    setDialogOpen(true);
  };

  const handleSave = () => {
    const err = validateForm(form, rows, editing);
    if (err) { toast.error(err); return; }
    saveMut.mutate({ id: editing?.id ?? null, values: toPayload(form) });
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortKategorien(rows).filter((k) =>
      (showInactive || k.aktiv) &&
      (!q || (k.name || '').toLowerCase().includes(q) || (k.schluessel || '').toLowerCase().includes(q)));
  }, [rows, search, showInactive]);

  const onToggle = (k, aktiv) => toggleMut.mutate({ id: k.id, aktiv });

  return (
    <PageShell
      head={(
        <PageHeader
          icon={<Recycle className="w-5 h-5" />}
          title="EWK-Kategorien"
          subtitle="Abgabesätze nach EWKFondsV (€/kg) · Einwegkunststofffonds"
          actions={(
            <Button onClick={() => openDialog()} className="bg-amber-500 hover:bg-amber-600 text-brand-950 font-bold h-11 px-5 rounded-xl gap-2">
              <Plus className="w-5 h-5" /> Neue Kategorie
            </Button>
          )}
        />
      )}
    >
      <InfoCard />

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Name oder Schlüssel suchen…" className="pl-9 h-11" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button variant="outline" onClick={() => setShowInactive((v) => !v)}
          className={cn('h-11', showInactive && 'bg-slate-100 border-slate-300')} aria-pressed={showInactive}>
          Inaktive anzeigen
        </Button>
      </div>

      {error ? (
        <Card className="border-2 border-red-300 bg-red-50">
          <CardContent className="p-4 flex items-start gap-3 text-sm text-red-800">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">EWK-Kategorien konnten nicht geladen werden.</p>
              <p className="mt-1 break-words">{error.message}</p>
              <p className="mt-1 text-red-700">{MIGRATION_HINT}</p>
            </div>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-16 text-slate-400">Lade EWK-Kategorien…</div>
      ) : rows.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-12 text-center text-slate-400">
            <Recycle className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p className="font-medium">Keine EWK-Kategorien.</p>
            <p className="text-xs mt-1">{MIGRATION_HINT}</p>
          </CardContent>
        </Card>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">Keine Treffer für die aktuelle Filterung.</div>
      ) : (
        <>
          <KategorieTable rows={visible} onToggle={onToggle} onEdit={openDialog} />
          <KategorieCards rows={visible} onToggle={onToggle} onEdit={openDialog} />
        </>
      )}

      <KategorieDialog
        open={dialogOpen} editing={editing} form={form} setForm={setForm}
        onClose={closeDialog} onSave={handleSave} isPending={saveMut.isPending}
      />
    </PageShell>
  );
}
